import React from "react";
import { Link } from "react-router-dom";
import { Heart, Flame, Droplet, Brain, Wheat, Sun, Bone, ShieldCheck, Flower2, Smile, ArrowRight } from "lucide-react";
import { HEALTH_BENEFITS } from "../mock";

const ICONS = { Heart, Flame, Droplet, Brain, Wheat, Sun, Bone, ShieldCheck, Flower2, Smile };

export default function HealthBenefitsPage() {
  return (
    <div className="bg-[#f4f0e6] text-[#3a3a32]">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-12 text-center">
        <p className="text-xs tracking-[0.4em] uppercase text-[#4a7ba1] mb-4">The clinical evidence</p>
        <h1 className="text-4xl md:text-6xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          The health benefits of high-polyphenol olive oil
        </h1>
        <p className="mt-8 text-lg text-[#5a5a52] leading-relaxed max-w-3xl mx-auto">
          Mediterranean cultures have consumed olive oil as medicine for over 4,000 years. Modern science has spent the last 30 years proving them right — across more than 2,000 peer-reviewed studies in journals like Nature, the New England Journal of Medicine, and JAMA Network Open.
        </p>
        <p className="mt-4 text-lg text-[#5a5a52] leading-relaxed max-w-3xl mx-auto">
          But the benefits are concentration-dependent. Most supermarket olive oils contain a fraction of the polyphenols used in clinical research. Fountain of Youth delivers 2,236 mg/kg of verified polyphenols in refrigerated, nitrogen-flushed monodose — the only format engineered to preserve what the science actually measured.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-24 space-y-16">
        {HEALTH_BENEFITS.map((b, i) => {
          const Icon = ICONS[b.icon] || Heart;
          const reverse = i % 2 === 1;
          return (
            <div key={i} className={`grid md:grid-cols-12 gap-8 items-center ${reverse ? "" : ""}`}>
              <div className={`md:col-span-4 ${reverse ? "md:order-2" : ""}`}>
                <div className="w-32 h-32 rounded-full bg-[#ece6d6] grid place-items-center mx-auto md:mx-0">
                  <Icon size={56} className="text-[#4a7ba1]" strokeWidth={1.3} />
                </div>
              </div>
              <div className={`md:col-span-8 ${reverse ? "md:order-1" : ""}`}>
                <div className="text-xs tracking-[0.3em] uppercase text-[#4a7ba1]">{b.category}</div>
                <h3 className="mt-3 text-3xl md:text-4xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <span className="text-[#1e3a52] block text-5xl md:text-6xl">{b.stat}</span>
                  <span className="text-[#3a3a32]">{b.headline}</span>
                </h3>
                <p className="mt-4 text-[#5a5a52] leading-relaxed">{b.body}</p>
                <p className="mt-3 text-sm italic text-[#4a7ba1]">{b.cite}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Why concentration matters */}
      <section className="bg-[#1e3a52] text-[#f4f0e6]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Why concentration & preservation matter</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Let food be thy medicine — but the medicine only works at the right dose.
          </h2>
          <p className="mt-6 text-[#dcd6c4] leading-relaxed">
            Every study above used olive oils with meaningful polyphenol concentrations. Most supermarket EVOOs contain 50–150 mg/kg. The EFSA health claim requires 250 mg/kg. The trials that produced the strongest results used oils above this threshold.
          </p>
          <div className="mt-10 inline-block border border-[#dcd6c4]/30 rounded-sm px-10 py-8">
            <div className="text-6xl md:text-7xl text-[#d4a857]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>2,236</div>
            <div className="text-sm tracking-widest uppercase text-[#dcd6c4] mt-1">mg/kg — Fountain of Youth verified concentration</div>
          </div>
          <div className="mt-10">
            <Link to="/waitlist" className="inline-flex items-center gap-2 bg-[#f4f0e6] text-[#1e3a52] hover:bg-white px-7 py-3 rounded-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}>
              Reserve the 2025 Harvest <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
